import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  Copy,
  ExternalLink
} from 'lucide-react'
import { type ComponentProps, useContext, useEffect, useMemo, useState } from 'react'

import BrokersMembersTransactionsContext from 'components/context/BrokersMembersTransactionsContext'
import { useGetTransactionsForEnrollment } from 'components/hooks/needs_attention/useGetTransactionsForEnrollment'
import CopyButtonWithAnimation from 'components/reusable/CopyButtonWithAnimation'
import { adminRecordInspector } from 'rails_routes'
import { Badge, Tooltip } from 'slab'
import { type ChargeOrTxn } from 'utils/web_api.schemas'

import {
  MarkTransactionAsLossDirectly,
  ReimbursementDetails,
  RemapEnrollmentPaymentDropdown,
  StripeCardDetails,
  ViewSubstantiation,
} from './BrokersMembersTransactionsActions'
import {
  getLocaleCurrencyStringFromString,
  getLocaleDateString,
} from '../../utils'
import {
  DisplayTextOnHoverTooltip,
} from './needs_attention/NeedsAttentionTable'
import TanstackTable, {
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<ChargeOrTxn>()

const columns = [
  columnHelper.accessor('id', {
    header: () => <div className='w-min'>ID</div>,
    cell: ({ getValue, row }) => {
      const txnId = getValue()
      const refundOriginalTransactionId = row.original.refund_original_transaction_id

      const renderBadge = ({ id, label, type, testId }: { id?: string, label?: string, type?: ComponentProps<typeof Badge>['type'], testId?: string }) => {
        return (
          <Badge
            data-testid={testId}
            onActionClick={() => window.open(adminRecordInspector.show({ id }))}
            actionIcon={ExternalLink}
            actionLabel={label}
            type={type}
          >
            {id?.slice(-6)}
          </Badge>
        )
      }

      const originalTransactionBadge = () => {
        if (!refundOriginalTransactionId) {
          return null
        }

        return (
          <Tooltip content='ID of transaction being refunded'>
            {renderBadge({ id: refundOriginalTransactionId, label: 'Inspect original transaction', type: 'warning', testId: 'refunded-transaction-id-badge' })}
          </Tooltip>
        )
      }

      return (
        <div className='flex flex-row items-center gap-2  '>
          <Tooltip content='ID of transaction'>
            {renderBadge({ id: txnId, label: 'Inspect original transaction', testId: 'transaction-id-badge' })}
          </Tooltip>
          {originalTransactionBadge()}
        </div>
      )
    },
  }),
  columnHelper.accessor('created_at', {
    header: () => <div className='w-min'>Date</div>,
    cell: ({ getValue }) => {
      return (
        <div className='text-gray-900 w-min'>
          {getLocaleDateString(new Date(getValue()))}
        </div>
      )
    },
  }),
  columnHelper.accessor('service_date', {
    header: () => <div>Service Date</div>,
    cell: ({ getValue }) => {
      const serviceDate = getValue()
      return (
        <div className='text-gray-900 w-min'>
          {serviceDate ? getLocaleDateString(new Date(serviceDate)) : ''}
        </div>
      )
    },
  }),
  columnHelper.accessor('amount', {
    header: () => <div className='text-right'>Amount</div>,
    cell: ({ row }) => {
      const { amount, loss_amount: lossAmount } = row.original
      const className =
        amount.subunits < 0
          ? 'text-red-600 text-right'
          : 'text-gray-900 text-right'

      // This is dumb. we need to align as an eng team how to deal with money amounts (both types and formatting).
      // Rather than change the API again rn or update our utils file, I'm going to leave this dumb thing as is and
      // raise the q in eng-weekly, that way whatever change I end up making is broadly aligned on.
      // Please shame @caroline if this code still exists in September 2024
      const formattedAmount = getLocaleCurrencyStringFromString(
        (amount.subunits / 100.0).toFixed(2)
      )

      let formattedLossAmount
      if (lossAmount && lossAmount.subunits > 0) {
        formattedLossAmount = getLocaleCurrencyStringFromString(
          (lossAmount.subunits / 100.0).toFixed(2)
        )
      }

      return (
        <div className={className}>
          <div className='flex items-center justify-end space-x-2'>
            <CopyButtonWithAnimation
              content={(amount.subunits / 100).toFixed(2)}
            >
              <Copy size={16} strokeWidth={1} />
            </CopyButtonWithAnimation>
            <div>{formattedAmount}</div>
          </div>
          {formattedLossAmount && (
            <p className='text-sm text-red-800'>Loss: {formattedLossAmount}</p>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor('payment_method_category', {
    header: 'Method',
    cell: ({ getValue, row }) => {
      let classes = ''
      let label = ''
      switch (getValue()) {
        case 'ach':
          classes = 'bg-yellow-100 text-yellow-800'
          label = 'ACH'
          break
        case 'reimbursement':
          classes = 'bg-fuchsia-100 text-fuchsia-600'
          label = 'Reimbursement'
          break
        case 'stripecard':
          classes = 'bg-cyan-100 text-cyan-800'
          label = 'Stripe'
          break
        case 'unitcard':
          classes = 'bg-orange-100 text-orange-700'
          label = 'Unit card'
          break
        default:
          classes = 'bg-gray-100 text-gray-800'
          label = 'Unknown'
      }

      const { external_source_last_4: last4 } = row.original

      return (
        <div className='flex item-center space-x-1'>
          <div
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}
          >
            {label}
          </div>
          {last4 && (
            <Tooltip content='Last 4 digits of card'>
              <Badge>{last4}</Badge>
            </Tooltip>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor('received_payment_id', {
    id: 'received_payment_id',
    header: 'Payment',
    cell: ({ getValue }) => {
      const receivedPaymentId = getValue()
      return (
        receivedPaymentId && (
          <Badge
            onActionClick={() =>
              window.open(adminRecordInspector.show({ id: receivedPaymentId }))}
            actionIcon={ExternalLink}
            actionLabel='Inspect enrollment'
          >
            {receivedPaymentId?.slice(-6)}
          </Badge>
        )
      )
    },
  }),
  columnHelper.accessor('received_payment_state', {
    id: 'received_payment_state',
    header: 'Payment State',
    cell: ({ getValue }) => {
      const state = getValue()

      let type: ComponentProps<typeof Badge>['type']
      let description = ''
      switch (state) {
        case 'completed':
          type = 'success'
          description = 'Payment successfully processed by both Thatch and the bank.'
          break
        case 'processing':
          type = 'slate'
          description = 'Payment currently being processed.'
          break
        case 'rejected':
          type = 'critical'
          description = 'Payment rejected by Thatch. Inspect payment for more details.'
          break
        case 'retrying':
          type = 'caution'
          description = 'Payment is being retried. Inspect payment for more details.'
          break
        case 'failed':
          type = 'warning'
          description = 'Payment failed due to internal error. Inspect payment for more details.'
          break
        case 'error':
          type = 'neutral'
          description = 'Payment is in an error state. Inspect payment for more details.'
          break
      }

      return (
        <div className='flex item-center space-x-1'>
          {
            state && (
              <Tooltip content={description}>
                <Badge type={type}>{state.charAt(0).toUpperCase() + state.slice(1)}</Badge>
              </Tooltip>
            )
          }
        </div>
      )
    },
  }),
  columnHelper.accessor('jit_ach_stage', {
    id: 'jit_ach_stage',
    header: 'Stage',
    cell: ({ getValue }) => {
      const stage = getValue()

      let description = ''
      switch (stage) {
        case 'attempt':
          description = 'Debit attempt made against account'
          break
        case 'complete':
          description = 'Debit completed successfully against account'
          break
        case 'reversal':
          description = 'Initial debit attempted reversed due to insufficient funds'
          break
        default:
          description = 'This could be a refund or another non-JIT ACH transaction and does not indicate an error.'
          break
      }

      return (
        <div className='flex item-center space-x-1'>
          {
            stage && (
              <Tooltip content={description}>
                <Badge>{stage.charAt(0).toUpperCase() + stage.slice(1)}</Badge>
              </Tooltip>
            )
          }
        </div>
      )
    },
  }),
  columnHelper.accessor('human_readable_summary', {
    header: 'Summary',
    cell: ({ getValue }) => {
      return (
        <div className='flex items-center'>
          <DisplayTextOnHoverTooltip
            text={getValue() || 'N/A'}
            classNames='max-w-40'
          />
        </div>
      )
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const { enrollmentId, memberId, declareLossesAtTransactionLevel } = useContext(
        BrokersMembersTransactionsContext
      )

      const isStripeCard = row.original.payment_method_category === 'stripecard'
      const isReimbursement = row.original.payment_method_category === 'reimbursement'

      return (
        <div className='flex item-center justify-start space-x-2 text-base'>
          <ViewSubstantiation transaction={row.original} />
          <RemapEnrollmentPaymentDropdown
            transaction={row.original}
            enrollmentId={enrollmentId}
            memberId={memberId}
          />

          {isStripeCard && <StripeCardDetails transaction={row.original} />}
          {isReimbursement && <ReimbursementDetails transaction={row.original} />}

          {declareLossesAtTransactionLevel && (
            <MarkTransactionAsLossDirectly
              transaction={row.original}
              memberId={memberId}
              enrollmentId={enrollmentId}
            />
          )}
        </div>
      )
    },
  }),
]

type BrokersMembersTransactionsTableProps = {
  pageParam?: number
  memberId: string
  enrollmentId: string
  isUserThatchAdmin: boolean
  isMemberTransactionsViewV2Enabled: boolean
  latestPlanYear: number
  declareLossesAtTransactionLevel: boolean
}

export function BrokersMembersTransactionsTableImpl ({
  pageParam,
  memberId,
  enrollmentId,
  isUserThatchAdmin,
  isMemberTransactionsViewV2Enabled,
  latestPlanYear,
  declareLossesAtTransactionLevel,
}: BrokersMembersTransactionsTableProps) {
  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isLoading, error } = useGetTransactionsForEnrollment({
    memberId,
    enrollmentId,
    pagination,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  const hasACHTxn = useMemo(() => {
    return Array.isArray(data?.data) && data.data.some((txn: ChargeOrTxn) => txn.payment_method_category === 'ach')
  }, [data])

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
      columnVisibility: {
        id: isUserThatchAdmin,
        received_payment_id: hasACHTxn,
        received_payment_state: hasACHTxn,
        jit_ach_stage: hasACHTxn,
      },
    },
  })

  return (
    <BrokersMembersTransactionsContext.Provider
      value={{
        enrollmentId,
        memberId,
        isUserThatchAdmin,
        isMemberTransactionsViewV2Enabled,
        latestPlanYear,
        declareLossesAtTransactionLevel,
      }}
    >
      {/* Table */}
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={Boolean(error)}
        noDataMessage='No transactions found'
        tableMessageHeightClass='h-16'
        paginationLabelSingular='record'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 1,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 1,
          })}
        loadingSkeletonPageSizeOverride={5}
      />
    </BrokersMembersTransactionsContext.Provider>
  )
}

const BrokersMembersTransactionsTable = (
  props: BrokersMembersTransactionsTableProps
) => (
  <BrokersMembersTransactionsTableImpl {...props} />
)

export default BrokersMembersTransactionsTable
